import type { LineString } from 'geojson';
import env from '~/env';

export type DirectionsResult = {
    /** Driving distance in kilometers */
    distanceKm: number;
    /** Driving duration in minutes */
    durationMin: number;
    /** GeoJSON LineString geometry for the route */
    geometry: LineString;
};

type MapboxDirectionsResponse = {
    routes: Array<{
        distance: number; // meters
        duration: number; // seconds
        geometry: LineString;
    }>;
    code: string;
};

const ENDPOINT = 'https://api.mapbox.com/directions/v5/mapbox/driving';

/**
 * In-memory route cache keyed by rounded coordinates (~1 km precision).
 * Avoids redundant Mapbox Directions API calls for nearby start/end points.
 * Entries expire after 24 hours. Module-scoped so it persists across renders
 * but not across full-page reloads (acceptable for a single session).
 */
const ROUTE_CACHE = new Map<string, { result: DirectionsResult; timestamp: number }>();
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
/** Cap the cache so a long session can't grow it without bound (oldest entry evicted first). */
const ROUTE_CACHE_MAX = 200;
const COORD_PRECISION = 2; // ~1.1 km at equator

function cacheKey(from: { lat: number; lng: number }, to: { lat: number; lng: number }): string {
    return [
        from.lat.toFixed(COORD_PRECISION),
        from.lng.toFixed(COORD_PRECISION),
        to.lat.toFixed(COORD_PRECISION),
        to.lng.toFixed(COORD_PRECISION),
    ].join(',');
}

/**
 * Fetch a driving route between two points using the Mapbox Directions API.
 * Returns the shortest route's distance (km), duration (min), and geometry.
 *
 * Results are cached in-memory by rounded coordinates so repeated lookups
 * for the same city pair don't hit the API again within the session.
 */
export async function fetchDrivingRoute(args: {
    from: { lat: number; lng: number };
    to: { lat: number; lng: number };
    signal?: AbortSignal;
}): Promise<DirectionsResult | null> {
    const token = env.NEXT_PUBLIC_MAPBOX_TOKEN;
    if (!token) return null;

    const key = cacheKey(args.from, args.to);
    const cached = ROUTE_CACHE.get(key);
    if (cached) {
        if (Date.now() - cached.timestamp < CACHE_TTL_MS) return cached.result;
        ROUTE_CACHE.delete(key); // expired — drop it instead of leaving it to linger.
    }

    const coordinates = `${args.from.lng},${args.from.lat};${args.to.lng},${args.to.lat}`;
    const url = new URL(`${ENDPOINT}/${coordinates}`);
    url.searchParams.set('access_token', token);
    url.searchParams.set('geometries', 'geojson');
    url.searchParams.set('overview', 'full');

    let response: Response;
    try {
        response = await fetch(url.toString(), { signal: args.signal });
    } catch {
        // Aborted (navigation / superseded request) or network failure — treat as "no route".
        return null;
    }
    if (!response.ok) return null;

    let data: MapboxDirectionsResponse;
    try {
        data = (await response.json()) as MapboxDirectionsResponse;
    } catch {
        // Malformed / non-JSON body — treat as "no route" instead of throwing.
        return null;
    }
    if (data.code !== 'Ok' || !data.routes?.length) return null;

    const route = data.routes[0];
    // Guard against a malformed route object (missing/non-numeric fields or no geometry).
    if (
        !route ||
        !Number.isFinite(route.distance) ||
        !Number.isFinite(route.duration) ||
        !route.geometry
    ) {
        return null;
    }
    const result: DirectionsResult = {
        distanceKm: route.distance / 1000,
        durationMin: route.duration / 60,
        geometry: route.geometry,
    };

    if (ROUTE_CACHE.size >= ROUTE_CACHE_MAX) {
        const oldest = ROUTE_CACHE.keys().next().value;
        if (oldest !== undefined) ROUTE_CACHE.delete(oldest);
    }
    ROUTE_CACHE.set(key, { result, timestamp: Date.now() });
    return result;
}
